import { useEffect, useState, useMemo, useRef, lazy, Suspense, type DragEvent, type Dispatch, type SetStateAction } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useApp } from '../store';
import { useServers, useChannels, useMembers, useServerVoiceState, useUnreads, useAudioDevices } from '../hooks';
import { api, clearToken } from '../api';
import { wsClient } from '../ws';
import { Avatar, Icon } from '../components/ui';
import { UserClickTarget } from '../components/UserPopover';
import { ContextMenu, type ContextMenuItem } from '../components/ContextMenu';
import { SelfBarTrigger } from '../components/SelfUserCard';
import { hasPermission, outranks, roleBadgeClass, roleLabel } from '../permissions';
import { syncAppBadge } from '../appBadge';

// Role rank for sorting (higher = more important)
function roleRank(role: string | null | undefined): number {
  switch (role) {
    case 'owner': return 4;
    case 'admin': return 3;
    case 'moderator': return 2;
    case 'member': return 1;
    default: return 0;
  }
}
import { TextChannelView } from '../components/TextChannelView';
import { VoiceChannelView } from '../components/VoiceChannelView';
import { NewMeetingModal } from '../components/NewMeetingModal';
import { useVoice } from '../components/voiceContext';
import { ViewerServerContext } from '../components/UserPopover';
import { useServerMutedIds, useVoiceMutesSync } from '../voiceMutes';
// SidebarVoiceBar lives in the heavy VoiceConnection module (pulls in LiveKit).
// Lazy-load it and only render it when the user is in a voice call, so text-only
// users never download the voice/LiveKit chunk.
const SidebarVoiceBar = lazy(() =>
  import('../components/VoiceConnection').then((m) => ({ default: m.SidebarVoiceBar })),
);
import { DmSidebar } from '../components/DmSidebar';
import { FriendsScreen } from './FriendsScreen';
import {
  CreateServerModal,
  JoinServerModal,
  CreateChannelModal,
  InviteCodeModal,
  SettingsModal,
  ManageMemberModal,
  ServerSettingsModal,
} from '../components/Modals';
import { EmojiManagerModal } from '../components/EmojiManagerModal';
import { CommandPalette } from '../components/CommandPalette';
import { ShortcutsModal } from '../components/ShortcutsModal';
import { primeAudio, playSound } from '../sounds';
import type { Server, Channel, ChannelCategory, Member, DmThread, VoiceParticipant } from '../types';
import { toast } from '../components/Toast';
import { ConfirmDialog, PromptDialog } from '../components/ConfirmDialog';

export function AppShell() {
  useVoiceMutesSync();
  // The URL param used to be the invite code; we've migrated to using the
  // server's UUID so invite codes don't leak in the URL bar / screen shares.
  // For backward compatibility we still accept invite codes in this slot
  // and redirect to the UUID URL below.
  const { serverId: urlServerId, channelSlug, dmChannelId } = useParams<{
    serverId?: string;
    channelSlug?: string;
    dmChannelId?: string;
  }>();
  const location = useLocation();
  const isDmMode = location.pathname.startsWith('/dm');
  const isFriendsMode = location.pathname.startsWith('/friends');
  // The DM/home sidebar (DM list + Friends entry) is shown for both.
  const isHomeMode = isDmMode || isFriendsMode;
  const navigate = useNavigate();
  const me = useApp((s) => s.user);
  const setUser = useApp((s) => s.setUser);
  const onlineUserIds = useApp((s) => s.onlineUserIds);
  // Gates the lazy SidebarVoiceBar: only mount (and thus fetch the voice chunk)
  // when actually in a call. SidebarVoiceBar already renders null otherwise.
  const inVoice = useApp((s) => s.activeVoice != null);
  const activeVoiceChannelId = useApp((s) => s.activeVoice?.channelId ?? null);
  const activeVoiceIsDm = useApp((s) => s.activeVoice?.isDm ?? false);

  const { servers, refresh: refreshServers, reorder: reorderServers } = useServers();

  // ---- Server rail drag-reorder (HTML5 DnD, desktop) --------------------------
  // `dragId` is the server being dragged; `dragOrder` holds the live working
  // order (ids, top-to-bottom) while a drag is in flight, null when idle. The
  // rail renders from `dragOrder` during a drag for instant visual reordering,
  // then persists once on drop.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);

  const railServers = useMemo(() => {
    if (!dragOrder) return servers;
    const byId = new Map(servers.map((s) => [s.id, s]));
    return dragOrder.map((id) => byId.get(id)).filter((s): s is (typeof servers)[number] => !!s);
  }, [dragOrder, servers]);

  function handleServerDragStart(e: DragEvent, id: string) {
    setDragId(id);
    setDragOrder(servers.map((s) => s.id));
    // Required for a drag to actually initiate in Firefox.
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', id);
    } catch {
      /* some browsers throw if setData is called late; harmless */
    }
  }

  function handleServerDragOver(e: DragEvent, targetId: string) {
    if (!dragId) return;
    e.preventDefault(); // allow the drop
    e.dataTransfer.dropEffect = 'move';
    if (dragId === targetId) return;
    setDragOrder((order) => {
      const base = order ?? servers.map((s) => s.id);
      const from = base.indexOf(dragId);
      const to = base.indexOf(targetId);
      if (from === -1 || to === -1 || from === to) return base;
      const next = [...base];
      next.splice(from, 1);
      next.splice(to, 0, dragId);
      return next;
    });
  }

  function handleServerDragEnd() {
    const finalOrder = dragOrder;
    const original = servers.map((s) => s.id).join(',');
    setDragId(null);
    setDragOrder(null);
    if (finalOrder && finalOrder.join(',') !== original) {
      reorderServers(finalOrder);
    }
  }

  // Resolve URL param -> server. Try UUID first, then fall back to invite code
  // (legacy URLs). If we matched on invite code, redirect to UUID below.
  const currentServer: Server | undefined = urlServerId
    ? servers.find((s) => s.id === urlServerId) ??
      servers.find((s) => s.inviteCode === urlServerId)
    : undefined;
  const matchedByInviteCode =
    !!urlServerId && !!currentServer && currentServer.id !== urlServerId;
  const serverId = currentServer?.id ?? null;

  // Migrate legacy URLs (invite code in path) to UUID-based URLs
  useEffect(() => {
    if (matchedByInviteCode && currentServer) {
      const dest = channelSlug
        ? `/s/${currentServer.id}/${channelSlug}`
        : `/s/${currentServer.id}`;
      navigate(dest, { replace: true });
    }
  }, [matchedByInviteCode, currentServer, channelSlug, navigate]);

  const { channels, categories, setChannels } = useChannels(serverId);
  // For moderator actions in UserPopover (which renders app-wide, so it reads
  // this from context rather than having the viewer's role drilled into it).
  const viewerServer = useMemo(
    () =>
      currentServer
        ? {
            serverId: currentServer.id,
            role: currentServer.role ?? null,
            voiceChannelIds: new Set(channels.filter((c) => c.type === 'voice').map((c) => c.id)),
          }
        : null,
    [currentServer, channels],
  );
  const members = useMembers(serverId);
  const { unreadByChannel, markReadLocally } = useUnreads();

  // Quick summary of DM unreads for the rail dot. We pull the user's DM list
  // and intersect with unreadByChannel — keeps the rail dot in sync without
  // having to special-case DM events in the unreads hook.
  const [dmThreadIds, setDmThreadIds] = useState<Set<string>>(new Set());
  // Track which channel ids we "know about" so we can detect when an
  // unread arrives for an unknown channel — typically a new DM thread
  // someone just opened with us. When that happens, refetch the DM list
  // so the badge starts including it.
  const knownChannelIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    api.listDms()
      .then((threads) => {
        if (cancelled) return;
        const ids = new Set(threads.map((t) => t.channelId));
        setDmThreadIds(ids);
        // Seed known set with our DM threads + active server channel ids.
        for (const id of ids) knownChannelIdsRef.current.add(id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [me]);

  // When unreads gain a key we don't know about, it's almost certainly a
  // new DM — refetch the thread list so the badge picks it up. Cheap
  // one-shot, only fires when an unknown channel id appears.
  useEffect(() => {
    let foundUnknown = false;
    for (const chId of Object.keys(unreadByChannel)) {
      if (!knownChannelIdsRef.current.has(chId)) {
        foundUnknown = true;
        knownChannelIdsRef.current.add(chId);
      }
    }
    if (foundUnknown) {
      api.listDms()
        .then((threads) => {
          setDmThreadIds(new Set(threads.map((t) => t.channelId)));
        })
        .catch(() => {});
    }
    // We intentionally only react to the keyset changing, not values, so
    // count bumps don't trigger refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(unreadByChannel).join('|')]);
  // Roll up unread state across DM threads for the server-rail badge.
  // Three subtleties:
  //   1. An entry can exist in unreadByChannel with unreadCount === 0
  //      (after marking read). We must check the count, not just presence.
  //   2. The DM thread the user is currently viewing doesn't count —
  //      same rule as for server channels (opening it marks it read).
  //   3. Only count threads we know about — dmThreadIds is the canonical
  //      set fetched at startup. New DMs from other users join it via WS
  //      events that update the threads list.
  const dmUnreadInfo = (() => {
    let unreadCount = 0;
    let mentions = 0;
    for (const [chId, state] of Object.entries(unreadByChannel)) {
      if (!dmThreadIds.has(chId)) continue;
      // Skip the DM thread we're currently viewing.
      if (isDmMode && chId === dmChannelId) continue;
      if (state.unreadCount > 0) unreadCount += state.unreadCount;
      if (state.mentionCount > 0) mentions += state.mentionCount;
    }
    return { hasUnread: unreadCount > 0 || mentions > 0, mentions, unreadCount };
  })();

  // App-icon badge (installed PWA): every DM unread + server-channel mentions —
  // the same set of events web push notifies about. Synced here because only
  // AppShell knows which channels are DM threads. Opening a channel marks it
  // read, which flows back through unreadByChannel and clears the badge.
  useEffect(() => {
    let total = 0;
    for (const [chId, st] of Object.entries(unreadByChannel)) {
      total += dmThreadIds.has(chId) ? st.unreadCount : st.mentionCount;
    }
    void syncAppBadge(total);
  }, [unreadByChannel, dmThreadIds]);

  // DM thread info — when in DM mode, fetch the other user's profile so the
  // header can show their name. Cheap one-shot per thread (the channel itself
  // is not server-bound so we don't need full channel metadata).
  const [dmThread, setDmThread] = useState<DmThread | null>(null);
  useEffect(() => {
    if (!isDmMode || !dmChannelId) {
      setDmThread(null);
      return;
    }
    let cancelled = false;
    api.listDms()
      .then((threads) => {
        if (cancelled) return;
        setDmThread(threads.find((t) => t.channelId === dmChannelId) ?? null);
      })
      .catch(() => {
        if (!cancelled) setDmThread(null);
      });
    return () => { cancelled = true; };
  }, [isDmMode, dmChannelId]);
  // List of voice channel IDs in this server — passed to useServerVoiceState
  // so it can filter incoming WS events to only this server's channels.
  const voiceChannelIds = useMemo(
    () => channels.filter((c) => c.type === 'voice').map((c) => c.id),
    [channels]
  );
  const voiceState = useServerVoiceState(serverId, voiceChannelIds);

  // Resolve channelSlug (channel name) -> channel -> id within current server
  const currentChannel: Channel | undefined = channelSlug
    ? channels.find((c) => c.name === channelSlug)
    : undefined;
  const channelId = currentChannel?.id ?? null;

  // True when the channel on screen IS the voice room we're in — the inline
  // room view is showing, so the minimized sidebar bar must stay hidden.
  // Deriving this from route state (not a post-paint DOM probe) makes it
  // synchronous, so the bar never flashes for a frame while LiveKit finishes
  // connecting right after you join a channel you're already looking at.
  const viewingActiveVoiceRoom =
    (!isDmMode && activeVoiceChannelId != null && channelId === activeVoiceChannelId) ||
    // In a DM call while looking at that same DM — the inline call grid takes
    // over, so the minimized sidebar bar must stay hidden.
    (isDmMode && activeVoiceChannelId != null && dmChannelId === activeVoiceChannelId);

  // True when we're in a 1:1 DM call for the DM currently on screen — render
  // the voice/video grid instead of the text thread.
  const inDmCall = isDmMode && activeVoiceIsDm && activeVoiceChannelId === dmChannelId;

  // When the active text channel changes, mark it as read. Effect runs once
  // per channel-id transition. We do this at AppShell rather than inside
  // TextChannelView so DMs (which also use TextChannelView via a synthetic
  // channel) are handled by their own effect below.
  useEffect(() => {
    if (!channelId || !currentChannel) return;
    if (currentChannel.type !== 'text') return;
    markReadLocally(channelId);
    api.markChannelRead(channelId).catch(() => {
      // Non-fatal — UI already cleared optimistically.
    });
  }, [channelId, currentChannel, markReadLocally]);

  // Same for DMs — when /dm/<channelId> is open, mark it read.
  useEffect(() => {
    if (!dmChannelId) return;
    markReadLocally(dmChannelId);
    api.markChannelRead(dmChannelId).catch(() => {});
  }, [dmChannelId, markReadLocally]);

  const [createServerOpen, setCreateServerOpen] = useState(false);
  const [joinServerOpen, setJoinServerOpen] = useState(false);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [serverSettingsOpen, setServerSettingsOpen] = useState(false);
  const [emojiManagerOpen, setEmojiManagerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [manageMember, setManageMember] = useState<Member | null>(null);

  // Global keyboard shortcuts: Cmd/Ctrl+K opens palette; "?" (or Cmd/Ctrl+/)
  // opens the shortcuts cheatsheet.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        primeAudio();  // priming audio context inside a user gesture
        setPaletteOpen((p) => !p);
        return;
      }
      // Shortcuts sheet: "?" (bare, not while typing) or Cmd/Ctrl+/.
      const target = e.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      const slash = (e.metaKey || e.ctrlKey) && e.key === '/';
      const question = e.key === '?' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey;
      if (slash || question) {
        e.preventDefault();
        setShortcutsOpen((s) => !s);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Prime audio on first click anywhere — required by browser autoplay policies
  useEffect(() => {
    function onFirstInteraction() {
      primeAudio();
      window.removeEventListener('pointerdown', onFirstInteraction);
    }
    window.addEventListener('pointerdown', onFirstInteraction, { once: true });
    return () => window.removeEventListener('pointerdown', onFirstInteraction);
  }, []);

  // Sound on member join/leave (only in the current server)
  useEffect(() => {
    if (!serverId) return;
    return wsClient.on((event) => {
      if (event.type === 'member.joined' && event.serverId === serverId && event.member.userId !== me?.id) {
        if (useApp.getState().soundsEnabled) playSound('join');
      } else if (event.type === 'member.left' && event.serverId === serverId && event.userId !== me?.id) {
        if (useApp.getState().soundsEnabled) playSound('leave');
      }
    });
  }, [serverId, me?.id]);

  // Mobile: which pane is showing
  const [mobilePane, setMobilePane] = useState<'sidebar' | 'main'>(channelSlug ? 'main' : 'sidebar');

  // WS connect is owned by App.tsx — this component just adds listeners.
  // Presence (hello + presence.update) is handled by AuthenticatedShell so
  // it survives navigating to /admin etc. We keep server.updated here
  // because it triggers a server list refetch which is AppShell-specific.
  useEffect(() => {
    return wsClient.on((event) => {
      if (event.type === 'server.updated') {
        // Server icon changed (or other top-level server fields). Refresh
        // the server list so the rail and sidebar pick up the new URL.
        refreshServers();
      }
    });
  }, [refreshServers]);

  // Auto-select first channel when entering a server, OR when the currently-
  // selected channel got deleted (channelSlug present but no match in channels)
  useEffect(() => {
    if (!currentServer || channels.length === 0) return;
    const noChannelOpen = !channelSlug;
    const channelGone = channelSlug && !channels.some((c) => c.name === channelSlug);
    if (noChannelOpen || channelGone) {
      const firstText = channels.find((c) => c.type === 'text') || channels[0];
      if (firstText) {
        navigate(`/s/${currentServer.id}/${firstText.name}`, { replace: true });
      }
    }
  }, [currentServer, channelSlug, channels, navigate]);

  // Mobile pane sync with route. Switching to a server-channel route OR a
  // DM route should bring the chat (main) pane into view on mobile. The
  // user already had to tap something to navigate, so we follow them
  // instead of leaving the sidebar showing.
  useEffect(() => {
    if (channelSlug || dmChannelId) setMobilePane('main');
  }, [channelSlug, dmChannelId]);

  function selectServer(server: Server) {
    navigate(`/s/${server.id}`);
    setMobilePane('sidebar');
  }

  function selectChannel(channel: Channel) {
    if (!currentServer) return;
    navigate(`/s/${currentServer.id}/${channel.name}`);
    setMobilePane('main');
  }

  function logout() {
    // End the session server-side too (so it leaves the sessions list and
    // the token can't be reused). Best-effort: the request captures the
    // token before clearToken() below, and a failure must not block logout.
    void api.logout().catch(() => {});
    clearToken();
    wsClient.disconnect();
    setUser(null);
  }

  async function refreshChannelsAndSelect(newChannelId: string) {
    if (!serverId) return;
    const fresh = await api.listChannels(serverId);
    setChannels(fresh);
    const ch = fresh.find((c) => c.id === newChannelId);
    if (ch) selectChannel(ch);
  }

  async function deleteChannel(c: Channel) {
    try {
      await api.deleteChannel(c.id);
      // Remove from local state immediately
      const remaining = channels.filter((ch) => ch.id !== c.id);
      setChannels(remaining);
      // If we just deleted the open channel, navigate to first remaining
      if (c.id === channelId && currentServer) {
        const fallback = remaining.find((ch) => ch.type === 'text') ?? remaining[0];
        if (fallback) {
          navigate(`/s/${currentServer.id}/${fallback.name}`, { replace: true });
        } else {
          navigate(`/s/${currentServer.id}`, { replace: true });
        }
      }
    } catch (err) {
      toast.error(err, 'Failed to delete channel');
    }
  }

  return (
    <ViewerServerContext.Provider value={viewerServer}>
    <div className="h-full w-full flex bg-bg overflow-hidden">
      {/* ====================== Server rail (far left) ====================== */}
      <div className="w-[64px] shrink-0 bg-bg border-r border-border-soft flex flex-col items-center pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        {/* Fixed top group — DM button + add menu. Pinned above the scrolling
            server list so they stay reachable no matter how many servers a
            user has joined. */}
        <div className="w-full flex flex-col items-center gap-2 pb-1">
          <button
            onClick={() => {
              navigate('/friends');
              // Mirror selectServer: tapping the rail switches context, so on
              // mobile land on the list (sidebar) rather than the main pane.
              // The Friends entry in that list then opens it in the main pane.
              setMobilePane('sidebar');
            }}
            className={`server-pill relative ${isHomeMode ? 'active' : ''}`}
            title="Friends & direct messages"
          >
            <Icon name="chat" size={20} />
            {dmUnreadInfo.hasUnread && (
              <span
                className={`absolute -top-0.5 -right-0.5 ${
                  dmUnreadInfo.mentions > 0
                    ? 'bg-danger text-white text-[9px] font-bold px-1 rounded-full leading-[1.4]'
                    : 'w-2.5 h-2.5 rounded-full bg-accent border-2 border-bg'
                }`}
              >
                {dmUnreadInfo.mentions > 0 ? dmUnreadInfo.mentions : null}
              </span>
            )}
          </button>
          <AddServerMenu
            onCreate={() => setCreateServerOpen(true)}
            onJoin={() => setJoinServerOpen(true)}
          />
          {/* Separator between the fixed actions and the scrolling servers. */}
          <div className="w-8 h-px bg-border-soft my-1" />
        </div>
        {/* Scrollable group — the user's joined servers. */}
        <div className="flex-1 min-h-0 w-full flex flex-col items-center gap-2 overflow-y-auto">
        {railServers.map((s) => {
          // Roll up unread state across this server's text channels. The
          // channel currently being viewed (if any) doesn't contribute —
          // opening it marks it read.
          const ids = s.textChannelIds ?? [];
          let unreadTotal = 0;
          let mentionTotal = 0;
          for (const cid of ids) {
            if (cid === channelId) continue;
            const u = unreadByChannel[cid];
            if (!u) continue;
            unreadTotal += u.unreadCount;
            mentionTotal += u.mentionCount;
          }
          const showMention = mentionTotal > 0;
          const showDot = !showMention && unreadTotal > 0;
          return (
          <button
            key={s.id}
            draggable
            onDragStart={(e) => handleServerDragStart(e, s.id)}
            onDragOver={(e) => handleServerDragOver(e, s.id)}
            onDragEnd={handleServerDragEnd}
            onDrop={(e) => {
              e.preventDefault();
              handleServerDragEnd();
            }}
            onClick={() => selectServer(s)}
            className={`server-pill relative ${s.id === serverId ? 'active' : ''} ${s.iconUrl ? 'p-0! overflow-hidden' : ''} ${dragId === s.id ? 'opacity-40' : ''} ${dragId ? 'cursor-grabbing' : ''}`}
            title={s.name}
          >
            {s.iconUrl ? (
              <img
                src={s.iconUrl}
                alt={s.name}
                draggable={false}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Hide broken image so initials fallback shows via parent
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              s.name.slice(0, 2).toUpperCase()
            )}
            {showMention && (
              <span
                className="absolute -top-0.5 -right-0.5 bg-danger text-white text-[9px] font-bold px-1 rounded-full leading-[1.4] border-2 border-bg pointer-events-none"
                title={`${mentionTotal} mention${mentionTotal === 1 ? '' : 's'}`}
              >
                {mentionTotal}
              </span>
            )}
            {showDot && (
              <span
                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent border-2 border-bg pointer-events-none"
                title={`${unreadTotal} unread`}
              />
            )}
          </button>
          );
        })}
        </div>
        {/* Pinned bottom — sign out, separated from the scrolling region.
            Placed in the rail (rather than the bottom-bar) so it's spatially
            isolated from settings/profile/etc — less chance of misclicks. */}
        {me && (
          <div className="w-full flex flex-col items-center gap-2 pt-2">
            <div className="w-8 h-px bg-border-soft" />
            <button
              onClick={logout}
              className="text-text-muted hover:text-danger flex items-center justify-center rounded-md transition-colors hover:bg-elevated"
              style={{ width: 26, height: 26 }}
              title="Sign off" aria-label="Sign off"
            >
              <Icon name="logout" size={12} />
            </button>
          </div>
        )}
      </div>

      {/* ====================== Channels + Members sidebar ====================== */}
      <div
        className={`aim-buddy-panel flex flex-col flex-1 min-w-0 md:flex-none md:w-[240px] md:shrink-0 border-r border-border ${
          mobilePane === 'sidebar' ? 'flex' : 'hidden'
        } md:flex`}
      >
        {isHomeMode ? (
          <DmSidebar
            activeChannelId={dmChannelId ?? null}
            onNavigateMain={() => setMobilePane('main')}
          />
        ) : currentServer ? (
          <>
            <div className="px-3 py-2.5 border-b border-border-soft bg-elevated/50 flex items-center justify-between gap-1">
              <div className="font-bold text-text truncate flex-1">{currentServer.name}</div>
              {hasPermission(currentServer.role, 'manage_server') && (
                <button
                  onClick={() => setServerSettingsOpen(true)}
                  className="nav-icon-btn"
                  title="Server settings"
                >
                  <Icon name="settings" size={12} />
                </button>
              )}
              {hasPermission(currentServer.role, 'manage_channels') && (
                <button
                  onClick={() => setEmojiManagerOpen(true)}
                  className="nav-icon-btn"
                  title="Manage custom emoji"
                >
                  <Icon name="smile" size={12} />
                </button>
              )}
              {hasPermission(currentServer.role, 'manage_members') && (
                <button
                  onClick={() => setInviteOpen(true)}
                  className="nav-icon-btn"
                  title="Invite people"
                >
                  <Icon name="plus" size={12} />
                </button>
              )}
            </div>

            {/* Channels list */}
            <div className="flex-1 overflow-y-auto py-2 min-h-0">
              <div className="px-3 mb-1 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold">
                  Channels
                </div>
                {hasPermission(currentServer.role, 'manage_channels') && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setCreatingCategory(true)}
                      className="text-text-subtle hover:text-text"
                      title="New category"
                      aria-label="New category"
                    >
                      <Icon name="folder-plus" size={12} />
                    </button>
                    <button
                      onClick={() => setCreateChannelOpen(true)}
                      className="text-text-subtle hover:text-text"
                      title="New channel"
                    >
                      <Icon name="plus" size={12} />
                    </button>
                  </div>
                )}
              </div>
              <div className="px-1">
                <ChannelTree
                  categories={categories}
                  channels={channels}
                  serverId={currentServer.id}
                  canManage={currentServer.role === 'owner' || currentServer.role === 'admin'}
                  voiceState={voiceState}
                  unreadByChannel={unreadByChannel}
                  activeChannelId={channelId}
                  onSelect={selectChannel}
                  onDelete={deleteChannel}
                  setChannels={setChannels}
                />
                {channels.length === 0 && (
                  <div className="text-xs text-text-subtle italic px-3 py-2">
                    No channels yet.
                  </div>
                )}
              </div>

              {/* Members */}
              <div className="px-3 mt-4 mb-1 text-[10px] uppercase tracking-widest text-text-subtle font-bold">
                Members — {members.length}
              </div>
              <div className="px-2 space-y-0.5">
                {members
                  .slice()
                  .sort((a, b) => {
                    // Higher role first (owner=4, admin=3, mod=2, member=1)
                    const rankDiff = roleRank(b.role) - roleRank(a.role);
                    if (rankDiff !== 0) return rankDiff;
                    // Online first
                    const aOn = onlineUserIds.has(a.userId) ? 0 : 1;
                    const bOn = onlineUserIds.has(b.userId) ? 0 : 1;
                    if (aOn !== bOn) return aOn - bOn;
                    return (a.displayName || a.username).localeCompare(b.displayName || b.username);
                  })
                  .map((m) => {
                    const isMe = m.userId === me?.id;
                    // Show ourselves the way others see us when invisible.
                    // Without this, the "Appear offline" toggle would have
                    // no visible effect in our own member list — confusing.
                    const selfVisible = !me?.isInvisible;
                    const status: 'online' | 'offline' =
                      (isMe ? selfVisible : onlineUserIds.has(m.userId)) ? 'online' : 'offline';
                    const canManage =
                      !isMe &&
                      hasPermission(currentServer.role, 'manage_members') &&
                      outranks(currentServer.role, m.role);
                    return (
                      <div
                        key={m.userId}
                        className={`group flex items-center gap-2 px-2 py-1 rounded text-sm hover:bg-black/5 dark:hover:bg-white/5 ${
                          status === 'offline' ? 'opacity-50' : ''
                        }`}
                      >
                        <UserClickTarget
                          userId={m.userId}
                          username={m.username}
                          displayName={m.displayName}
                          avatarUrl={m.avatarUrl}
                          serverRole={m.role}
                          joinedAt={m.joinedAt}
                          className="flex items-center gap-2 flex-1 min-w-0 hover:cursor-pointer"
                        >
                          <Avatar
                            name={m.displayName || m.username}
                            url={m.avatarUrl}
                            size={20}
                            status={status}
                          />
                          <span className="truncate text-text flex-1 min-w-0">
                            {m.displayName || m.username}
                            {isMe && <span className="text-text-subtle text-xs ml-1">(you)</span>}
                          </span>
                        </UserClickTarget>
                        {m.role !== 'member' && (
                          <span
                            className={`inline-flex items-center justify-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm min-w-[42px] ${roleBadgeClass(m.role)}`}
                            title={roleLabel(m.role)}
                          >
                            {m.role === 'moderator' ? 'mod' : m.role}
                          </span>
                        )}
                        {/* Manage-button slot — always reserved so the badge
                            column lines up across rows, regardless of whether
                            the row is the current user (no manage button) or
                            someone else (manage button hidden until hover). */}
                        <div className="w-4 shrink-0 flex items-center justify-center">
                          {canManage && (
                            <button
                              onClick={() => setManageMember(m)}
                              className="opacity-0 group-hover:opacity-100 text-text-subtle hover:text-text leading-none transition-opacity"
                              title="Manage member"
                            >
                              ⋯
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="text-4xl mb-2">👋</div>
            <div className="font-bold text-text">No server selected</div>
            <div className="text-xs text-text-muted mt-1">
              Pick a server from the left, or make one.
            </div>
          </div>
        )}

        {/* Voice mini-panel — shown when in voice but NOT currently viewing
            the room (viewing it shows the inline grid instead). */}
        {inVoice && !viewingActiveVoiceRoom && (
          <Suspense fallback={null}>
            <SidebarVoiceBar />
          </Suspense>
        )}

        {/* Bottom bar — current user + theme toggle */}
        <div className="border-t border-border-soft bg-elevated/50 p-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] flex items-center gap-2">
          {me && (
            <>
              {/* Clicking your avatar/name opens the self user-card (profile
                  actions + notifications). The avatar dot is the online
                  indicator; an unread-notification badge sits on top. */}
              <SelfBarTrigger />
              {me.isSuperAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="nav-icon-btn text-accent!"
                  title="Admin dashboard"
                >
                  <Icon name="grid" size={16} />
                </button>
              )}
              <SelfMuteDeafenControls />
              <button
                onClick={() => setSettingsOpen(true)}
                className="nav-icon-btn"
                title="Settings"
              >
                <Icon name="settings" size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ====================== Main content area ====================== */}
      <div
        className={`flex-1 min-w-0 bg-surface ${mobilePane === 'main' ? 'flex' : 'hidden'} md:flex flex-col`}
      >
        {/* Mobile back button */}
        <div className="md:hidden border-b border-border bg-surface px-3 py-2">
          <button
            onClick={() => setMobilePane('sidebar')}
            className="aim-button shadow-none!"
          >
            <Icon name="back" size={14} /> Back
          </button>
        </div>

        {isFriendsMode ? (
          <FriendsScreen />
        ) : isDmMode ? (
          dmChannelId && dmThread ? (
            inDmCall ? (
              <VoiceChannelView
                channel={{
                  id: dmChannelId,
                  serverId: null,
                  name: dmThread.otherUser.displayName || dmThread.otherUser.username,
                  type: 'voice',
                  topic: null,
                  position: 0,
                  createdAt: dmThread.createdAt,
                }}
                serverId=""
              />
            ) : (
              <TextChannelView
                channel={{
                  id: dmChannelId,
                  serverId: null,
                  name:
                    dmThread.otherUser.displayName ||
                    dmThread.otherUser.username,
                  type: 'dm',
                  topic: null,
                  position: 0,
                  createdAt: dmThread.createdAt,
                }}
                serverId={null}
                myRole={null}
              />
            )
          ) : (
            <DmWelcome />
          )
        ) : currentChannel ? (
          currentChannel.type === 'text' ? (
            <TextChannelView channel={currentChannel} serverId={serverId!} myRole={currentServer?.role ?? null} />
          ) : (
            <VoiceChannelView
              channel={currentChannel}
              serverId={serverId!}
              preloadedParticipants={voiceState[currentChannel.id]}
            />
          )
        ) : (
          <Welcome />
        )}
      </div>

      {/* ====================== Modals ====================== */}
      <CreateServerModal
        open={createServerOpen}
        onClose={() => setCreateServerOpen(false)}
        onCreated={(id) => {
          refreshServers().then((fresh) => {
            const created = fresh.find((s) => s.id === id);
            if (created) navigate(`/s/${created.id}`);
          });
        }}
      />
      <JoinServerModal
        open={joinServerOpen}
        onClose={() => setJoinServerOpen(false)}
        onJoined={(id) => {
          refreshServers().then((fresh) => {
            const joined = fresh.find((s) => s.id === id);
            if (joined) navigate(`/s/${joined.id}`);
          });
        }}
      />
      {serverId && (
        <CreateChannelModal
          open={createChannelOpen}
          onClose={() => setCreateChannelOpen(false)}
          serverId={serverId}
          onCreated={refreshChannelsAndSelect}
        />
      )}
      {currentServer && (
        <InviteCodeModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          serverId={currentServer.id}
          code={currentServer.inviteCode}
          serverName={currentServer.name}
          canRotate={currentServer.role === 'owner' || currentServer.role === 'admin'}
        />
      )}
      {currentServer && (
        <ServerSettingsModal
          open={serverSettingsOpen}
          onClose={() => setServerSettingsOpen(false)}
          serverId={currentServer.id}
          serverName={currentServer.name}
          currentIconUrl={currentServer.iconUrl ?? null}
          onUpdated={(changes) => {
            // Refresh the servers list so the rail updates
            refreshServers();
            void changes;
          }}
        />
      )}
      {currentServer && (
        <EmojiManagerModal
          open={emojiManagerOpen}
          onClose={() => setEmojiManagerOpen(false)}
          serverId={currentServer.id}
          serverName={currentServer.name}
        />
      )}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      {currentServer && (
        <ManageMemberModal
          open={!!manageMember}
          onClose={() => setManageMember(null)}
          serverId={currentServer.id}
          member={manageMember}
          myRole={currentServer.role}
        />
      )}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        servers={servers}
        onNavigate={(server, channel) => {
          navigate(`/s/${server.id}/${channel.name}`);
          setMobilePane('main');
        }}
        onOpenSettings={() => {
          setPaletteOpen(false);
          setSettingsOpen(true);
        }}
      />
      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <VoiceHotkeys />

      <PromptDialog
        open={creatingCategory}
        title="New category"
        label="Category name"
        placeholder="e.g. Voice Channels"
        confirmLabel="Create"
        onConfirm={(name) => {
          setCreatingCategory(false);
          if (currentServer) {
            void api
              .createCategory(currentServer.id, name)
              .catch((err) => toast.error(err, 'Failed to create category'));
          }
        }}
        onCancel={() => setCreatingCategory(false)}
      />
    </div>
    </ViewerServerContext.Provider>
  );
}

function Welcome() {
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-8">
      <div className="hybrid-card w-full max-w-md">
        <div className="hybrid-card-header">Welcome</div>
        <div className="p-8 text-center bg-surface space-y-3">
          <div className="text-5xl">👋</div>
          <div className="font-bold text-xl text-text">Pick a server to start chatting</div>
          <div className="text-sm text-text-muted">
            Choose a server on the left, or create one with the <Icon name="plus" size={12} className="inline align-middle" /> button.
          </div>
        </div>
      </div>
    </div>
  );
}

function DmWelcome() {
  const [meetingOpen, setMeetingOpen] = useState(false);
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-8">
      <div className="hybrid-card w-full max-w-md">
        <div className="hybrid-card-header">Direct Messages</div>
        <div className="p-8 text-center bg-surface space-y-3">
          <div className="text-5xl">💬</div>
          <div className="font-bold text-xl text-text">Pick a conversation</div>
          <div className="text-sm text-text-muted">
            Or click + to start a new direct message with someone.
          </div>
          {/* The one entry point for instant meetings. It lives here rather
              than in the server rail because a meeting isn't a server — it's
              the "call someone who isn't on here" path, which is the same
              intent that brings people to the DM home. */}
          <div className="pt-3 border-t border-border-soft">
            <button
              type="button"
              onClick={() => setMeetingOpen(true)}
              className="aim-button px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5"
            >
              <Icon name="link" size={14} />
              Start a meeting
            </button>
            <p className="text-[11px] text-text-subtle mt-2">
              Get a link anyone can join — no account needed.
            </p>
          </div>
        </div>
      </div>
      <NewMeetingModal open={meetingOpen} onClose={() => setMeetingOpen(false)} />
    </div>
  );
}

// =============================================================================
// VoiceParticipantList — sidebar participant rows under a voice channel
// =============================================================================
// Enriches each row with live speaking glow + mute icon when this happens to
// be the voice channel we're currently connected to. For other voice channels
// in the same server (we're not in their LiveKit room), we render plain rows
// since we have no live track data — only server-side presence.
//
// Designed to be cheap: useVoice() is called once for the whole list. The
// per-participant lookups are O(participants in our room), not O(all rooms).
// =============================================================================
function VoiceParticipantList({
  serverId,
  channelId,
  participants,
}: {
  serverId: string | null;
  channelId: string;
  participants: VoiceParticipant[];
}) {
  const voice = useVoice();
  const activeVoice = useApp((s) => s.activeVoice);
  // Only enrich rows when this list belongs to the channel we're connected to.
  const isOurRoom = voice.inRoom && activeVoice?.channelId === channelId;
  // Server-wide, so it shows for every voice channel, not just ours.
  const serverMutedIds = useServerMutedIds(serverId);

  return (
    <div className="ml-7 space-y-0.5 mt-0.5 mb-1 animate-fade-in">
      {participants.map((p) => {
        const name = p.displayName || p.username;
        // Speaking + mute are only available for participants in our LiveKit
        // room. For other rooms we fall back to neutral rendering.
        const speaking = isOurRoom ? voice.isSpeaking(p.userId) : false;
        const muted = isOurRoom ? voice.isMuted(p.userId) : false;
        const deafened = isOurRoom ? voice.isDeafened(p.userId) : false;
        const serverMuted = serverMutedIds.includes(p.userId);
        return (
          <UserClickTarget
            key={p.userId}
            userId={p.userId}
            username={p.username}
            displayName={p.displayName}
            avatarUrl={p.avatarUrl}
            className={`w-full flex items-center gap-1.5 px-1.5 py-0.5 rounded text-xs transition-colors ${
              speaking
                ? 'bg-online/10 text-text font-medium'
                : 'text-text-muted'
            }`}
          >
            <div
              className={`rounded-full transition-shadow ${
                speaking ? 'ring-2 ring-online' : ''
              }`}
            >
              <Avatar name={name} url={p.avatarUrl} size={16} />
            </div>
            <span className="truncate flex-1">{name}</span>
            {p.isSharing && (
              <span title="Sharing screen" className="shrink-0 inline-flex">
                <Icon name="monitor" size={11} className="text-accent" />
              </span>
            )}
            {/* Deafen implies mute, so only show one icon when both apply.
                The headphones-off glyph is the more informative state —
                others know the user can't hear AND can't speak. */}
            {deafened ? (
              <span title="Deafened" className="shrink-0 inline-flex">
                <Icon name="headphones-off" size={11} className="text-danger/80" />
              </span>
            ) : serverMuted ? (
              <span title="Server muted by a moderator" className="shrink-0 inline-flex">
                <Icon name="mic-off" size={11} className="text-[#D97706]" />
              </span>
            ) : muted ? (
              <Icon
                name="mic-off"
                size={11}
                className="text-text-subtle shrink-0"
              />
            ) : null}
          </UserClickTarget>
        );
      })}
    </div>
  );
}

// =============================================================================
// ChannelSectionList — text or voice section, with drag-to-reorder for
// owners/admins.
// =============================================================================
// Drag UX: HTML5 dnd. Picking up a row sets the drag image and source id.
// Hovering over another row shows an insertion-line indicator (top or bottom
// of the target depending on cursor position). Drop reorders optimistically
// and POSTs the new order; the server then broadcasts and any other clients
// re-render via the WS handler in useChannels.
//
// We DON'T attach drag handlers when the user can't reorder — those rows are
// inert with no draggable affordance. Renders cleanly for everyone.
// =============================================================================
// =============================================================================
// ChannelTree — Discord-style mixed channel list grouped into categories.
// Channels carry their own #/🔊 icon; categories are collapsible and hold a
// mix of types. Uncategorized channels render at the bottom with no header.
// Owners/admins can drag channels to reorder within / move between categories,
// and create/rename/delete categories.
// =============================================================================
function ChannelTree({
  categories,
  channels,
  serverId,
  canManage,
  voiceState,
  unreadByChannel,
  activeChannelId,
  onSelect,
  onDelete,
  setChannels,
}: {
  categories: ChannelCategory[];
  channels: Channel[];
  serverId: string;
  canManage: boolean;
  voiceState: Record<string, VoiceParticipant[]>;
  unreadByChannel: Record<string, { unreadCount: number; mentionCount: number }>;
  activeChannelId: string | null;
  onSelect: (c: Channel) => void;
  onDelete: (c: Channel) => void;
  setChannels: Dispatch<SetStateAction<Channel[]>>;
}) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: string; before: boolean } | null>(null);
  const [dropCatKey, setDropCatKey] = useState<string | null>(null); // category id or '__uncat__'
  const [ctxMenu, setCtxMenu] = useState<{ channel: Channel; x: number; y: number } | null>(null);
  const [catMenu, setCatMenu] = useState<{ category: ChannelCategory; x: number; y: number } | null>(null);
  // Dialog state is hoisted to the tree, not the rows — channels and categories
  // are .map-rendered, so a dialog per row would mount one per channel. Same
  // rule the context menus above already follow.
  const [pendingDeleteChannel, setPendingDeleteChannel] = useState<Channel | null>(null);
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState<ChannelCategory | null>(null);
  const [renamingCategory, setRenamingCategory] = useState<ChannelCategory | null>(null);

  // Collapsed categories, persisted per server.
  const collapseKey = `chatapp_catcollapsed_${serverId}`;
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(collapseKey);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });
  function toggleCollapsed(catId: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      try {
        localStorage.setItem(collapseKey, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  // Group channels by category, each sorted by position.
  const byCat = new Map<string | null, Channel[]>();
  for (const c of channels) {
    const k = c.categoryId ?? null;
    const arr = byCat.get(k);
    if (arr) arr.push(c);
    else byCat.set(k, [c]);
  }
  for (const arr of byCat.values()) arr.sort((a, b) => a.position - b.position);
  const sortedCats = [...categories].sort((a, b) => a.position - b.position);
  const uncategorized = (byCat.get(null) ?? []).slice();

  function reset() {
    setDraggingId(null);
    setDropTarget(null);
    setDropCatKey(null);
  }

  async function commitMove(categoryId: string | null, orderedIds: string[]) {
    const snapshot = channels;
    setChannels((prev) => {
      const orderMap = new Map(orderedIds.map((id, i) => [id, (i + 1) * 10] as const));
      return prev.map((c) =>
        orderMap.has(c.id) ? { ...c, categoryId, position: orderMap.get(c.id)! } : c
      );
    });
    try {
      await api.moveChannel(serverId, { categoryId, orderedIds });
    } catch (err) {
      console.error('moveChannel failed', err);
      setChannels(snapshot); // revert
    }
  }

  function dropOnChannel(target: Channel, before: boolean) {
    if (!draggingId || draggingId === target.id) return reset();
    const destCat = target.categoryId ?? null;
    const ids = (byCat.get(destCat) ?? []).map((c) => c.id).filter((id) => id !== draggingId);
    let idx = ids.indexOf(target.id);
    if (!before) idx += 1;
    ids.splice(idx, 0, draggingId);
    commitMove(destCat, ids);
    reset();
  }

  function dropOnCategory(categoryId: string | null) {
    if (!draggingId) return reset();
    const ids = (byCat.get(categoryId) ?? []).map((c) => c.id).filter((id) => id !== draggingId);
    ids.push(draggingId);
    commitMove(categoryId, ids);
    reset();
  }

  function renderChannel(c: Channel) {
    const isVoice = c.type === 'voice';
    const participants = isVoice ? (voiceState[c.id] ?? []) : [];
    const unread = unreadByChannel[c.id];
    const isActive = c.id === activeChannelId;
    const showUnread = !isActive && unread && unread.unreadCount > 0;
    const showMention = !isActive && unread && unread.mentionCount > 0;
    const isDragging = draggingId === c.id;
    const isDropTarget = dropTarget?.id === c.id;

    return (
      <div key={c.id} className="group/channel relative">
        {isDropTarget && dropTarget.before && (
          <div className="absolute -top-px left-1 right-1 h-0.5 bg-accent rounded-full pointer-events-none" />
        )}
        <div
          draggable={canManage}
          onDragStart={(e) => {
            if (!canManage) return;
            setDraggingId(c.id);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', c.id);
          }}
          onDragEnd={reset}
          onDragOver={(e) => {
            if (!canManage || !draggingId || draggingId === c.id) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const before = e.clientY < rect.top + rect.height / 2;
            setDropTarget((cur) => (cur && cur.id === c.id && cur.before === before ? cur : { id: c.id, before }));
          }}
          onDrop={(e) => {
            if (!canManage) return;
            e.preventDefault();
            e.stopPropagation();
            const before = dropTarget?.id === c.id ? dropTarget.before : true;
            dropOnChannel(c, before);
          }}
          onClick={() => onSelect(c)}
          onContextMenu={(e) => {
            e.preventDefault();
            setCtxMenu({ channel: c, x: e.clientX, y: e.clientY });
          }}
          className={`channel-link ${isActive ? 'active' : ''} ${showUnread ? 'text-text! font-semibold' : ''} ${isDragging ? 'opacity-40' : ''} ${canManage ? 'cursor-grab active:cursor-grabbing' : ''}`}
        >
          {c.type === 'text' ? (
            <span className="font-bold text-base leading-none opacity-60">#</span>
          ) : (
            <Icon name="speaker" size={13} />
          )}
          <span className="truncate flex-1">{c.name}</span>
          {showMention && (
            <span className="bg-danger text-white text-[9px] font-bold px-1.5 rounded-full leading-[1.4]">
              {unread!.mentionCount}
            </span>
          )}
          {showUnread && !showMention && <span className="w-1.5 h-1.5 rounded-full bg-accent" />}
          {isVoice && participants.length > 0 && (
            <span className="text-[10px] text-text-subtle font-mono">{participants.length}</span>
          )}
          {canManage && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPendingDeleteChannel(c);
              }}
              className="opacity-0 group-hover/channel:opacity-100 hover:text-danger px-1 leading-none transition-opacity"
              title="Delete channel"
              aria-label={`Delete #${c.name}`}
            >
              ×
            </button>
          )}
        </div>
        {isDropTarget && !dropTarget.before && (
          <div className="absolute -bottom-px left-1 right-1 h-0.5 bg-accent rounded-full pointer-events-none" />
        )}
        {isVoice && participants.length > 0 && (
          <VoiceParticipantList serverId={serverId} channelId={c.id} participants={participants} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {sortedCats.map((cat) => {
        const isCollapsed = collapsed.has(cat.id);
        const catChannels = byCat.get(cat.id) ?? [];
        const isCatDrop = dropCatKey === cat.id;
        return (
          <div key={cat.id}>
            <div
              onDragOver={(e) => {
                if (!canManage || !draggingId) return;
                e.preventDefault();
                setDropCatKey(cat.id);
              }}
              onDragLeave={() => setDropCatKey((k) => (k === cat.id ? null : k))}
              onDrop={(e) => {
                if (!canManage) return;
                e.preventDefault();
                dropOnCategory(cat.id);
              }}
              className={`rounded-sm ${isCatDrop ? 'bg-accent/15 ring-1 ring-accent/40' : ''}`}
            >
              <button
                type="button"
                onClick={() => toggleCollapsed(cat.id)}
                onContextMenu={(e) => {
                  if (!canManage) return;
                  e.preventDefault();
                  setCatMenu({ category: cat, x: e.clientX, y: e.clientY });
                }}
                className="w-full px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-widest text-text-subtle hover:text-text font-bold flex items-center gap-1 transition-colors"
              >
                <Icon name="chevron-down" size={10} className={`transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                <span className="truncate">{cat.name}</span>
              </button>
            </div>
            {!isCollapsed && <div className="space-y-0.5">{catChannels.map(renderChannel)}</div>}
          </div>
        );
      })}

      {/* Uncategorized channels — a drop zone with no visible header. */}
      <div
        onDragOver={(e) => {
          if (!canManage || !draggingId) return;
          e.preventDefault();
          setDropCatKey('__uncat__');
        }}
        onDragLeave={() => setDropCatKey((k) => (k === '__uncat__' ? null : k))}
        onDrop={(e) => {
          if (!canManage) return;
          e.preventDefault();
          dropOnCategory(null);
        }}
        className={`rounded-sm mt-0.5 min-h-[8px] ${dropCatKey === '__uncat__' ? 'bg-accent/15 ring-1 ring-accent/40' : ''}`}
      >
        {sortedCats.length > 0 && uncategorized.length > 0 && (
          <div className="px-2 pt-1.5 pb-0.5 text-[10px] uppercase tracking-widest text-text-subtle font-bold">
            Other
          </div>
        )}
        <div className="space-y-0.5">{uncategorized.map(renderChannel)}</div>
      </div>

      {ctxMenu &&
        (() => {
          const c = ctxMenu.channel;
          const hasUnread = (unreadByChannel[c.id]?.unreadCount ?? 0) > 0;
          const items: ContextMenuItem[] = [
            {
              label: 'Mark as Read',
              icon: 'check',
              disabled: !hasUnread,
              onClick: () => void api.markChannelRead(c.id).catch(() => {}),
            },
            {
              label: 'Copy Link',
              icon: 'copy',
              onClick: () =>
                void navigator.clipboard.writeText(`${window.location.origin}/s/${serverId}/${c.name}`).catch(() => {}),
            },
          ];
          if (canManage) {
            items.push({ type: 'separator' }, {
              label: 'Delete Channel',
              icon: 'trash',
              danger: true,
              onClick: () => setPendingDeleteChannel(c),
            });
          }
          return <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={items} onClose={() => setCtxMenu(null)} />;
        })()}

      {catMenu &&
        (() => {
          const cat = catMenu.category;
          const items: ContextMenuItem[] = [
            {
              label: 'Rename Category',
              icon: 'edit',
              onClick: () => setRenamingCategory(cat),
            },
            { type: 'separator' },
            {
              label: 'Delete Category',
              icon: 'trash',
              danger: true,
              onClick: () => setPendingDeleteCategory(cat),
            },
          ];
          return <ContextMenu x={catMenu.x} y={catMenu.y} items={items} onClose={() => setCatMenu(null)} />;
        })()}

      <ConfirmDialog
        open={!!pendingDeleteChannel}
        title="Delete channel"
        body={
          <>
            Delete <b>#{pendingDeleteChannel?.name}</b>? Its messages and attachments go with it.
            This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (pendingDeleteChannel) onDelete(pendingDeleteChannel);
          setPendingDeleteChannel(null);
        }}
        onCancel={() => setPendingDeleteChannel(null)}
      />

      <ConfirmDialog
        open={!!pendingDeleteCategory}
        title="Delete category"
        body={
          <>
            Delete <b>{pendingDeleteCategory?.name}</b>? Its channels aren't deleted — they become
            uncategorized.
          </>
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          const cat = pendingDeleteCategory;
          setPendingDeleteCategory(null);
          if (cat) {
            void api.deleteCategory(cat.id).catch((err) => toast.error(err, 'Failed to delete category'));
          }
        }}
        onCancel={() => setPendingDeleteCategory(null)}
      />

      <PromptDialog
        open={!!renamingCategory}
        title="Rename category"
        label="Category name"
        initialValue={renamingCategory?.name ?? ''}
        confirmLabel="Rename"
        onConfirm={(name) => {
          const cat = renamingCategory;
          setRenamingCategory(null);
          if (cat && name !== cat.name) {
            void api.renameCategory(cat.id, name).catch((err) => toast.error(err, 'Failed to rename category'));
          }
        }}
        onCancel={() => setRenamingCategory(null)}
      />
    </div>
  );
}

// =============================================================================
// AddServerMenu — single "+" trigger that pops a small menu offering
// "Create a new server" or "Join with invite code".
// =============================================================================
// Replaces the two stacked buttons in the server rail. Anchored to the right
// of the trigger so the rail-tooltip placement makes sense.
// =============================================================================
function AddServerMenu({
  onCreate,
  onJoin,
}: {
  onCreate: () => void;
  onJoin: () => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Computed once when opening: position the popover next to the trigger in
  // viewport coords. We use position:fixed so the popover escapes the
  // server rail's overflow-y-auto clipping (otherwise it renders behind
  // sibling columns or gets cut off at the rail edge).
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  function toggle() {
    if (!open) {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setPos({ left: r.right + 8, top: r.top });
    }
    setOpen((o) => !o);
  }

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node | null;
      if (menuRef.current?.contains(t)) return;
      if (triggerRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={toggle}
        className="server-pill"
        title="Add a server" aria-label="Add a server"
      >
        <Icon name="plus" size={20} />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          className="fixed z-1000 hybrid-card w-56"
          style={{ left: pos.left, top: pos.top }}
        >
          <div className="hybrid-card-header py-1.5!">
            <span>Add a server</span>
          </div>
          <div className="bg-surface p-1 space-y-0.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onCreate();
              }}
              className="w-full flex items-start gap-2 px-2 py-2 rounded-sm text-left hover:bg-elevated transition-colors"
            >
              <Icon name="plus" size={14} className="mt-0.5 text-text-muted shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text">Create a new server</div>
                <div className="text-[10px] text-text-muted">Start one for your group</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onJoin();
              }}
              className="w-full flex items-start gap-2 px-2 py-2 rounded-sm text-left hover:bg-elevated transition-colors"
            >
              <Icon name="server" size={14} className="mt-0.5 text-text-muted shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-text">Join with invite code</div>
                <div className="text-[10px] text-text-muted">Use a code from a friend</div>
              </div>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// =============================================================================
// SelfMuteDeafenControls — bottom-bar mic + headphones toggles.
// =============================================================================
// Always rendered, but greyed-out when not in voice. Active state shows
// as a danger-tinted button so users can tell at a glance whether they're
// muted/deafened. When in a call, clicking is the fastest way to toggle
// without opening the voice tile.
// =============================================================================
function SelfMuteDeafenControls() {
  // Mic mute / deafen only do anything once you're in a voice room, so only
  // surface them then — keeps the bottom user-bar uncluttered the rest of the
  // time instead of showing two dead, disabled buttons. The inner component
  // owns the device enumeration so we don't poll devices for text-only users.
  if (!useVoice().inRoom) return null;
  return <VoiceControlsInner />;
}

function VoiceControlsInner() {
  const voice = useVoice();
  const audioDevices = useApp((s) => s.audioDevices);
  const setAudioDevices = useApp((s) => s.setAudioDevices);
  // In a call mic permission is already granted, so device labels are
  // populated — no "show names" prompt needed here.
  const { inputs, outputs, outputSupported } = useAudioDevices();
  return (
    <>
      <VoiceDeviceButton
        active={voice.muted}
        iconName={voice.muted ? 'mic-off' : 'mic'}
        onToggle={() => { void voice.toggleMute(); }}
        toggleTitle={voice.serverMuted ? 'Muted by a moderator' : voice.muted ? 'Unmute mic' : 'Mute mic'}
        menuTitle="Input device"
        devices={inputs}
        selectedId={audioDevices.micDeviceId}
        onSelect={(id) => setAudioDevices({ micDeviceId: id })}
      />
      <VoiceDeviceButton
        active={voice.deafened}
        iconName={voice.deafened ? 'headphones-off' : 'headphones'}
        onToggle={() => { void voice.toggleDeafen(); }}
        toggleTitle={voice.deafened ? 'Undeafen — hear others again' : 'Deafen — silence everyone (also mutes your mic)'}
        menuTitle="Output device"
        devices={outputs}
        selectedId={audioDevices.outputDeviceId}
        onSelect={(id) => setAudioDevices({ outputDeviceId: id })}
        // setSinkId (output switching) isn't supported in every browser; when
        // it isn't, drop the caret so we don't offer a no-op picker.
        menuDisabled={!outputSupported}
      />
    </>
  );
}

// A bottom-bar voice button (mic/headphones): left-click toggles mute/deafen,
// and a small corner caret opens a device picker — Discord-style. Writing the
// pick to the store is enough; VoiceConnection's switchActiveDevice effects
// hot-swap the live device mid-call.
function VoiceDeviceButton({
  active,
  iconName,
  onToggle,
  toggleTitle,
  menuTitle,
  devices,
  selectedId,
  onSelect,
  menuDisabled,
}: {
  active: boolean;
  iconName: string;
  onToggle: () => void;
  toggleTitle: string;
  menuTitle: string;
  devices: { deviceId: string; label: string; isDefault: boolean }[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  menuDisabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Resolve the active selection: a null/unknown id (e.g. unplugged device)
  // falls through to "System default" so the checkmark never dangles.
  const known = devices.some((d) => d.deviceId === selectedId);
  const effectiveId = selectedId && known ? selectedId : null;

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        onClick={onToggle}
        className={`nav-icon-btn ${
          active ? 'text-danger!' : ''
        }`}
        title={toggleTitle} aria-label={toggleTitle}
      >
        <Icon name={iconName} size={16} />
      </button>
      {!menuDisabled && (
        <button
          onClick={() => setOpen((o) => !o)}
          className={`absolute -top-1 -right-1 z-10 w-3.5 h-3.5 inline-flex items-center justify-center rounded-full border border-border bg-panel text-text-muted hover:text-text hover:bg-elevated ${
            open ? 'text-text! bg-elevated!' : ''
          }`}
          title={`Choose ${menuTitle.toLowerCase()}`}
          aria-label={`Choose ${menuTitle.toLowerCase()}`}
        >
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={9} />
        </button>
      )}
      {open && (
        <div className="absolute bottom-full right-0 mb-2 z-50 hybrid-card w-56 max-h-72 overflow-y-auto">
          <div className="hybrid-card-header py-1.5! text-[11px]!">{menuTitle}</div>
          <div className="bg-surface py-1">
            <DeviceMenuItem
              label="System default"
              selected={effectiveId === null}
              onClick={() => { onSelect(null); setOpen(false); }}
            />
            {devices.map((d) => (
              <DeviceMenuItem
                key={d.deviceId}
                label={d.label + (d.isDefault ? ' (system default)' : '')}
                selected={effectiveId === d.deviceId}
                onClick={() => { onSelect(d.deviceId); setOpen(false); }}
              />
            ))}
            {devices.length === 0 && (
              <div className="px-3 py-2 text-[11px] text-text-subtle italic">
                No devices found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DeviceMenuItem({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-elevated text-text"
    >
      <span className="w-3 shrink-0 text-link">
        {selected && <Icon name="check" size={12} />}
      </span>
      <span className="truncate flex-1">{label}</span>
    </button>
  );
}

// Mod+Shift+M / Mod+Shift+D toggle mute / deafen while in a call. Its own
// null-rendering component (not a branch in AppShell's key handler) because
// the voice api churns on every speaking-state flip — reading it in AppShell
// would re-render the whole shell each time someone talks. Fires even while
// typing: a Mod+Shift chord can't collide with text entry.
function VoiceHotkeys() {
  const voice = useVoice();
  useEffect(() => {
    if (!voice.inRoom) return;
    function onKey(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if (e.code === 'KeyM') {
        e.preventDefault();
        void voice.toggleMute();
      } else if (e.code === 'KeyD') {
        e.preventDefault();
        void voice.toggleDeafen();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [voice.inRoom, voice.toggleMute, voice.toggleDeafen]);
  return null;
}
